'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import Link from 'next/link'
import { useTranslations, useLocale } from 'next-intl'
import {
  Search,
  Filter,
  Plus,
  Grid3X3,
  List,
  Columns,
  Download,
  SortAsc,
  SortDesc,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Minus,
  ChevronDown,
  X,
  Calendar,
  User,
  Tag,
  Clock,
  FileText,
  Bug,
  Lightbulb,
  MoreHorizontal,
  Edit,
  Copy,
  Trash2,
  Check,
  Eye,
  Play,
  FolderOpen,
  Loader2
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { getAuthHeaders } from '@/lib/api-utils'
import { getTrackerBadgeStyle, getStatusBadgeStyle, getPriorityBadgeStyle } from '@/lib/badge-utils'
import { IssueTimeTracker } from '@/components/issues/issue-time-tracker'
import dynamic from 'next/dynamic'
import { IssueCard } from '@/components/issues/issue-card'
import { IssueCardGrid } from '@/components/issues/issue-card-grid'
import { IssueFilterSidebar } from '@/components/issues/issue-filter-sidebar'
import { VirtualizedIssuesTable } from '@/components/issues/virtualized-issues-table'
import { cn } from '@/lib/utils'

// Lazy load heavy modals to reduce initial bundle size
const EditIssueModal = dynamic(
  () => import('@/components/issues/edit-issue-modal').then(mod => ({ default: mod.EditIssueModal })),
  { ssr: false, loading: () => null }
)

const IssueActionsModal = dynamic(
  () => import('@/components/issues/issue-actions-modal').then(mod => ({ default: mod.IssueActionsModal })),
  { ssr: false, loading: () => null }
)
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCurrentUser,
  useProjects,
  useTrackers,
  useIssueStatuses,
  usePriorities,
  useProjectVersions,
} from '@/hooks/use-common-api'

interface Issue {
  id: number
  subject: string
  description: string
  tracker: {
    id: number
    name: string
  }
  status: {
    id: number
    name: string
    is_closed: boolean
  }
  priority: {
    id: number
    name: string
  }
  author: {
    id: number
    name: string
  }
  assigned_to?: {
    id: number
    name: string
  }
  category?: {
    id: number
    name: string
  }
  fixed_version?: {
    id: number
    name: string
  }
  parent?: {
    id: number
    subject: string
  }
  children?: Issue[]
  project: {
    id: number
    name: string
  }
  start_date?: string
  due_date?: string
  done_ratio: number
  estimated_hours?: number
  spent_hours?: number
  created_on: string
  updated_on: string
  custom_fields?: Array<{
    id: number
    name: string
    value: string
  }>
}

interface Project {
  id: number
  name: string
  identifier: string
  description?: string
  status: number
}

interface FilterState {
  status: string[]
  tracker: string[]
  priority: string[]
  assignee: string[]
  version: string[]
  author: string[]
  category: string[]
  parent: string[]
}

interface SortConfig {
  field: keyof Issue | 'tracker.name' | 'status.name' | 'priority.name' | 'assigned_to.name'
  direction: 'asc' | 'desc'
}

interface IssuesListViewProps {
  projectId?: string  // Optional - if not provided, shows all issues
  showProjectSelector?: boolean  // Show project dropdown for filtering
  onRefreshTrigger?: number  // External trigger to force refresh
}

export function IssuesListView({ projectId, showProjectSelector = false, onRefreshTrigger }: IssuesListViewProps) {
  const t = useTranslations('issuesPage')
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'
  const [issues, setIssues] = useState<Issue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Use React Query hooks for cached API data - eliminates duplicate calls!
  const { data: currentUserData, isLoading: loadingCurrentUser } = useCurrentUser()
  const { data: projectsData, isLoading: loadingProjects } = useProjects(1000)
  const { data: trackersData, isLoading: loadingTrackers } = useTrackers()
  const { data: statusesData, isLoading: loadingStatuses } = useIssueStatuses()
  const { data: prioritiesData, isLoading: loadingPriorities } = usePriorities()
  
  // Project selector state - Persistent selected project
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    // If projectId prop is provided, use it (for project-specific pages)
    if (projectId) return projectId
    // Otherwise load from localStorage
    if (typeof window !== 'undefined' && showProjectSelector) {
      return localStorage.getItem('issues-selected-project') || 'all'
    }
    return 'all'
  })
  
  // Load versions for selected project (conditional query)
  const effectiveProjectId = showProjectSelector ? selectedProjectId : projectId
  const { data: versionsData } = useProjectVersions(
    effectiveProjectId && effectiveProjectId !== 'all' ? effectiveProjectId : null
  )
  
  // Extract data from React Query hooks with defaults
  const currentUser = useMemo(() => {
    if (!currentUserData) return null
    return {
      id: currentUserData.id,
      name: currentUserData.name
    }
  }, [currentUserData])
  
  const projects = useMemo(() => projectsData?.projects || [], [projectsData])
  const trackers = useMemo(() => trackersData || [], [trackersData])
  const statuses = useMemo(() => statusesData || [], [statusesData])
  const priorities = useMemo(() => prioritiesData || [], [prioritiesData])
  const versions = useMemo(() => versionsData || [], [versionsData])
  
  // Persistent search term
  const [searchTerm, setSearchTerm] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('issues-search-term') || ''
    }
    return ''
  })
  
  // Debounced search term - only triggers fetch after user stops typing
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm)
  
  // Debounce search term to prevent fetching on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 800) // 800ms delay - wait for user to stop typing
    
    return () => clearTimeout(timer)
  }, [searchTerm])
  
  // Persistent view mode
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'table'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('issues-view-mode')
      if (saved === 'list' || saved === 'grid' || saved === 'table') {
        return saved
      }
    }
    return 'table'
  })
  
  const [showFilters, setShowFilters] = useState(false)
  
  // Persistent sort config
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('issues-sort-config')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error('Failed to parse saved sort config:', e)
        }
      }
    }
    return { field: 'updated_on', direction: 'desc' }
  })

  // Client-side sort state for inline table (< 50 issues)
  const [inlineTableSort, setInlineTableSort] = useState<{
    column: 'id' | 'subject' | 'status' | 'assignee' | null
    direction: 'asc' | 'desc'
  }>({ column: null, direction: 'asc' })

  // Handle inline table sort
  const handleInlineTableSort = (column: 'id' | 'subject' | 'status' | 'assignee') => {
    if (inlineTableSort.column === column) {
      setInlineTableSort({
        column,
        direction: inlineTableSort.direction === 'asc' ? 'desc' : 'asc'
      })
    } else {
      setInlineTableSort({ column, direction: 'asc' })
    }
  }

  // Sort issues for inline table
  const sortedInlineIssues = useMemo(() => {
    if (!inlineTableSort.column || issues.length >= 50) return issues

    return [...issues].sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (inlineTableSort.column) {
        case 'id':
          aValue = a.id
          bValue = b.id
          break
        case 'subject':
          aValue = a.subject.toLowerCase()
          bValue = b.subject.toLowerCase()
          break
        case 'status':
          aValue = a.status?.name.toLowerCase() || ''
          bValue = b.status?.name.toLowerCase() || ''
          break
        case 'assignee':
          aValue = a.assigned_to?.name.toLowerCase() || 'zzz'
          bValue = b.assigned_to?.name.toLowerCase() || 'zzz'
          break
        default:
          return 0
      }

      if (aValue < bValue) return inlineTableSort.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return inlineTableSort.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [issues, inlineTableSort])
  const [sidebarMenu, setSidebarMenu] = useState<{
    show: boolean
    issue: Issue | null
  }>({ show: false, issue: null })
  const [menuData, setMenuData] = useState<any>(null)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null)
  const [showCreateIssueModal, setShowCreateIssueModal] = useState(false)
  const [showEditIssueModal, setShowEditIssueModal] = useState(false)
  const [issueToEdit, setIssueToEdit] = useState<Issue | null>(null)

  // Project search state
  const [projectSearch, setProjectSearch] = useState('')
  
  // Users list (extracted from issues)
  const [users, setUsers] = useState<{id: number, name: string}[]>([])

  // Active filters - Load from localStorage on init
  const [filters, setFilters] = useState<FilterState>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('issues-filters')
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch (e) {
          console.error('Failed to parse saved filters:', e)
        }
      }
    }
    return {
      status: [],
      tracker: [],
      priority: [],
      assignee: [],
      version: [],
      author: [],
      category: [],
      parent: []
    }
  })

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-filters', JSON.stringify(filters))
    }
  }, [filters])

  // Memoized callbacks to prevent unnecessary re-renders in child components
  const handleOpenMenu = useCallback((issue: any) => {
    setSidebarMenu({ show: true, issue })
    setMenuData({ statuses, priorities, trackers, members: users })
  }, [statuses, priorities, trackers, users])

  const handleCloseMenu = useCallback(() => {
    setSidebarMenu({ show: false, issue: null })
  }, [])

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false)
  }, [])

  // Memoized search handler
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  // Memoized project search handler
  const handleProjectSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setProjectSearch(e.target.value)
  }, [])

  // Save search term to localStorage (debounced)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-search-term', debouncedSearchTerm)
    }
  }, [debouncedSearchTerm])

  // Save view mode to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-view-mode', viewMode)
    }
  }, [viewMode])

  // Save sort config to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('issues-sort-config', JSON.stringify(sortConfig))
    }
  }, [sortConfig])

  // Save selected project to localStorage (only if showProjectSelector is true)
  useEffect(() => {
    if (typeof window !== 'undefined' && showProjectSelector && !projectId) {
      localStorage.setItem('issues-selected-project', selectedProjectId)
    }
  }, [selectedProjectId, showProjectSelector, projectId])

  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const itemsPerPage = 25

  // Note: currentUser, projects, trackers, statuses, priorities, versions 
  // are now loaded automatically via React Query hooks!
  // No need for manual fetch functions or useEffect calls

  // Refetch issues when user returns to the page (e.g., after creating an issue)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refetch issues
        fetchIssues()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [currentPage, sortConfig, filters, debouncedSearchTerm, selectedProjectId, projectId])

  const extractUsersFromIssues = (issues: any[]) => {
    const userMap = new Map<number, {id: number, name: string}>()

    issues.forEach(issue => {
      // Add assigned_to user
      if (issue.assigned_to) {
        if (!userMap.has(issue.assigned_to.id)) {
          userMap.set(issue.assigned_to.id, {
            id: issue.assigned_to.id,
            name: issue.assigned_to.name
          })
        }
      }

      // Add author if present
      if (issue.author) {
        if (!userMap.has(issue.author.id)) {
          userMap.set(issue.author.id, {
            id: issue.author.id,
            name: issue.author.name
          })
        }
      }
    })

    const extractedUsers = Array.from(userMap.values())
    if (extractedUsers.length > 0) {
      setUsers(extractedUsers)
    }
  }

  // Fetch project members for all projects present in the provided issues
  const fetchMembersForIssues = async (issuesList: any[]) => {
    try {
      const authHeaders = getAuthHeaders()

      // Collect project IDs from issues
      const projectIds = Array.from(new Set(issuesList.map(i => i.project?.id).filter(Boolean)))
      if (projectIds.length === 0) return

      // Fetch memberships for each project in parallel
      const promises = projectIds.map(id =>
        fetch(`/api/projects/${id}/members`, { headers: authHeaders }).then(async (res) => {
          if (!res.ok) return null
          try {
            return await res.json()
          } catch {
            return null
          }
        }).catch(() => null)
      )

      const results = await Promise.all(promises)

      const userMap = new Map<number, {id: number, name: string}>()

      // Add users from project memberships
      results.forEach(result => {
        if (!result) return
        // Redmine memberships.json returns { memberships: [ { user: { id, name } } ] }
        const memberships = result.memberships || result.members || []
        memberships.forEach((m: any) => {
          const u = m.user || m.member || m.user_id && { id: m.user_id, name: m.name }
          if (u && u.id && !userMap.has(u.id)) {
            userMap.set(u.id, { id: u.id, name: u.name })
          }
        })
      })

      // Also add assignees/authors from issues in case some are not in memberships
      issuesList.forEach(issue => {
        if (issue.assigned_to && issue.assigned_to.id && !userMap.has(issue.assigned_to.id)) {
          userMap.set(issue.assigned_to.id, { id: issue.assigned_to.id, name: issue.assigned_to.name })
        }
        if (issue.author && issue.author.id && !userMap.has(issue.author.id)) {
          userMap.set(issue.author.id, { id: issue.author.id, name: issue.author.name })
        }
      })

      // Use currentUser from React Query hook (no duplicate API call!)
      if (currentUser && currentUser.id) {
        userMap.set(currentUser.id, { id: currentUser.id, name: currentUser.name })
      }

      // Build array and put current user first (if present)
      const allUsers = Array.from(userMap.values())
      let sortedUsers = allUsers
      
      if (currentUser && currentUser.id) {
        sortedUsers = [
          { id: currentUser.id, name: currentUser.name },
          ...allUsers.filter(u => u.id !== currentUser.id)
        ]
      }

      if (sortedUsers.length > 0) {
        setUsers(sortedUsers)
      }
    } catch (err) {
      // ignore failures silently - fallback extraction will apply
    }
  }

  const fetchIssues = async () => {
    // fetchIssues called
    const startTime = performance.now()
    
    // Don't clear issues immediately - this prevents the skeleton from showing
    // and losing focus on the search input
    setLoading(true)
    setError(null)

    try {
      const authHeaders = getAuthHeaders()
      if (!authHeaders || Object.keys(authHeaders).length === 0) {
        // No client-side authentication available. Set an error state and abort
        // the fetch instead of throwing - this avoids noisy stack traces in
        // production and lets the UI render a friendly access/unauthenticated state.
        setError('no_auth')
        return
      }

      // Build query parameters
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: ((currentPage - 1) * itemsPerPage).toString(),
        sort: `${sortConfig.field}:${sortConfig.direction}`,
        _t: Date.now().toString() // Cache-busting timestamp
      })

      // Add project filter - use selectedProjectId if showProjectSelector is true, otherwise use projectId
      const effectiveProjectId = showProjectSelector ? selectedProjectId : projectId
      if (effectiveProjectId && effectiveProjectId !== 'all') {
        params.append('project_id', effectiveProjectId)
      }

      if (debouncedSearchTerm) {
        params.append('subject', `~${debouncedSearchTerm}`)
      }

      // Add filters
      if (filters.status.length > 0) {
        params.append('status_id', filters.status.join('|'))
      }
      if (filters.tracker.length > 0) {
        params.append('tracker_id', filters.tracker.join('|'))
      }
      if (filters.priority.length > 0) {
        params.append('priority_id', filters.priority.join('|'))
      }
      if (filters.assignee.length > 0) {
        params.append('assigned_to_id', filters.assignee.join('|'))
      }
      if (filters.author.length > 0) {
        params.append('author_id', filters.author.join('|'))
      }
      if (filters.version.length > 0) {
        params.append('fixed_version_id', filters.version.join('|'))
      }

      // Fetching issues with params
      const response = await fetch(`/api/issues?${params}`, {
        headers: authHeaders,
        cache: 'no-store'
      })

      if (!response.ok) {
        // Handle 403 Forbidden - Permission Denied
        if (response.status === 403) {
          setError('permission_denied')
          setIssues([])
          setTotalCount(0)
          return
        }
        throw new Error(`Failed to fetch issues: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
  // Received issues count
      
      // Ensure issues are ordered by most recently updated first as a client-side safety-net
      const fetched = data.issues || []
      const sorted = fetched.slice().sort((a: Issue, b: Issue) => {
        const ta = new Date(a.updated_on).getTime()
        const tb = new Date(b.updated_on).getTime()
        return tb - ta
      })
      setIssues(sorted)
      setTotalCount(data.total_count || 0)

      // Fetch members in parallel (non-blocking) to speed up initial render
      if ((data.issues || []).length > 0) {
        // Don't await - let it happen in background
        Promise.resolve().then(() => {
          fetchMembersForIssues(data.issues || [])
          // Also fall back to extracting direct users from issues if memberships fail
          if (users.length === 0) {
            extractUsersFromIssues(data.issues || [])
          }
        })
      }
      
  const endTime = performance.now()
  // fetchIssues completed in elapsed ms
    } catch (err) {
      console.error('[IssuesListView] fetchIssues error:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  // Note: Filter options (trackers, statuses, priorities, versions) are now loaded
  // automatically via React Query hooks - no need for separate fetch functions!

  // Populate users list whenever issues change
  useEffect(() => {
    if (issues.length > 0) {
      extractUsersFromIssues(issues)
    }
  }, [issues])

  useEffect(() => {
    fetchIssues()
  }, [currentPage, sortConfig, filters, debouncedSearchTerm, selectedProjectId, projectId, onRefreshTrigger])

  // Show project name under each issue only when there is no specific project selected.
  // If a project is selected via the dropdown (selectedProjectId !== 'all'), or
  // if this view is already scoped to a project (projectId provided), do not show the project name.
  const shouldShowProjectName = showProjectSelector ? (selectedProjectId === 'all') : (!projectId)

  const clearFilters = () => {
    setFilters({
      status: [],
      tracker: [],
      priority: [],
      assignee: [],
      version: [],
      author: [],
      category: [],
      parent: []
    })
  }

  const getActiveFilterCount = () => {
    return Object.values(filters).reduce((acc, curr) => acc + curr.length, 0)
  }

  const handleProjectSelect = useCallback((projectId: string) => {
    // Project selected
    
    // Immediately clear issues and set loading to show skeleton
    setIssues([])
    setLoading(true)
    
    setSelectedProjectId(projectId)
    setCurrentPage(1)
    setFilters({
      status: [],
      tracker: [],
      priority: [],
      assignee: [],
      version: [],
      author: [],
      category: [],
      parent: []
    })
  }, [])

  const filteredProjects = useMemo(() => {
    return projects.filter(project =>
      project.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
      project.identifier.toLowerCase().includes(projectSearch.toLowerCase())
    )
  }, [projects, projectSearch])

  if (loading && issues.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 flex items-center gap-4">
            <Skeleton className="h-10 max-w-sm flex-1 rounded-md" />
            {showProjectSelector && (
              <Skeleton className="h-10 w-72 rounded-md" />
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-9 w-24 rounded-md" />
            <Skeleton className="h-9 w-32 rounded-md" />
            {!showProjectSelector && projectId && (
              <Skeleton className="h-9 w-28 rounded-md" />
            )}
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="overflow-x-auto rounded-md border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-background">
              <tr>
                <th className="px-2 py-2 text-left font-medium text-muted-foreground w-16">
                  <Skeleton className="h-4 w-4 rounded-full" />
                </th>
                <th className="px-2 py-2 text-left font-medium text-muted-foreground max-w-xs lg:max-w-md">
                  <Skeleton className="h-4 w-16 rounded-full" />
                </th>
                <th className="px-2 py-2 text-left font-medium text-muted-foreground w-36">
                  <Skeleton className="h-4 w-12 rounded-full" />
                </th>
                <th className="px-2 py-2 text-left font-medium text-muted-foreground w-44 hidden md:table-cell">
                  <Skeleton className="h-4 w-16 rounded-full" />
                </th>
                <th className="px-2 py-2 text-center font-medium text-muted-foreground w-12 sticky right-0 bg-background"></th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <tr key={i} className="hover:bg-muted/5">
                  <td className="px-2 py-2 align-middle">
                    <Skeleton className="h-4 w-8 rounded-full" />
                  </td>
                  <td className="px-2 py-2 align-middle max-w-xs lg:max-w-md">
                    <Skeleton className="h-4 w-full rounded-full mb-1" />
                    {shouldShowProjectName && (
                      <Skeleton className="h-3 w-32 rounded-full" />
                    )}
                  </td>
                  <td className="px-2 py-2 align-middle">
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </td>
                  <td className="px-2 py-2 align-middle hidden md:table-cell">
                    <Skeleton className="h-4 w-24 rounded-full" />
                  </td>
                  <td className="px-2 py-2 align-middle sticky right-0 bg-background/95 backdrop-blur-sm">
                    <div className="flex justify-center">
                      <Skeleton className="h-8 w-8 rounded-md" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  if (error === 'permission_denied') {
    return (
      <div className="space-y-6">
        {/* Header with Project Selector - Always Visible */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 flex items-center gap-4">
            <Input
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              className="max-w-sm"
              disabled
            />

            {/* Project Selector - Keep visible so user can switch projects */}
            {showProjectSelector && (
              <Select
                value={selectedProjectId}
                onValueChange={handleProjectSelect}
              >
                <SelectTrigger className="w-[280px]">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="truncate">
                      <SelectValue placeholder={t('allProjects')} />
                    </div>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className={cn('absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground', isRTL ? 'right-2.5' : 'left-2.5')} />
                      <Input
                        placeholder={t('searchProjectsPlaceholder')}
                        value={projectSearch}
                        onChange={handleProjectSearchChange}
                        className="pl-8 h-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('allProjects')}</span>
                    </div>
                  </SelectItem>
                  {filteredProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium truncate">{project.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {filteredProjects.length === 0 && projectSearch && (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {t('noProjectsFound')}
                    </div>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Access Denied Error Card */}
        <Card className="border-2 border-red-200 dark:border-red-800">
          <CardContent className="py-16 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg text-red-900 dark:text-red-100">
                  {t('accessDeniedTitle')}
                </h3>
                <p className="text-muted-foreground">
                  {t('accessDeniedMessage')}
                </p>
                {showProjectSelector && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('trySelectingDifferentProject') || 'Try selecting a different project from the dropdown above.'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Empty state: No projects available
  if (showProjectSelector && projects.length === 0 && !loading) {
    return (
      <Card className="border-2 border-dashed border-muted-foreground/20">
        <CardContent className="py-16 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
              <FolderOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">
                {t('noProjectAccessTitle')}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {t('noProjectAccessMessage')}
              </p>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => window.location.href = '/projects'}>
                <FolderOpen className="h-4 w-4 mr-2" />
                {t('viewProjectsButton')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Input
                placeholder={t('searchPlaceholder')}
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full"
              />
              {loading && issues.length > 0 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Project Selector */}
            {showProjectSelector && (
              <Select
                value={selectedProjectId}
                onValueChange={handleProjectSelect}
              >
                <SelectTrigger className="w-[280px]">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="truncate">
                      <SelectValue placeholder={t('allProjects')} />
                    </div>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className={cn('absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground', isRTL ? 'right-2.5' : 'left-2.5')} />
                      <Input
                        placeholder={t('searchProjectsPlaceholder')}
                        value={projectSearch}
                        onChange={handleProjectSearchChange}
                        className="pl-8 h-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{t('allProjects')}</span>
                    </div>
                  </SelectItem>
                  {filteredProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id.toString()}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium truncate">{project.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                  {filteredProjects.length === 0 && projectSearch && (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      {t('noProjectsFound')}
                    </div>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="relative"
            >
              <Filter className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
              {t('filtersTitle')}
              {getActiveFilterCount() > 0 && (
                <Badge variant="secondary" className={cn(isRTL ? 'mr-2' : 'ml-2', 'px-1.5 py-0.5 text-xs')}>
                  {getActiveFilterCount()}
                </Badge>
              )}
            </Button>

            <div className="flex items-center space-x-1 border rounded-md overflow-hidden">
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="px-3 rounded-none h-9"
              >
                <Columns className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="px-3 rounded-none h-9"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="px-3 rounded-none h-9"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
            </div>

            {!showProjectSelector && projectId && (
              <Button onClick={() => setShowCreateIssueModal(true)} size="sm">
                  <Plus className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                  {t('newIssue')}
                </Button>
            )}
          </div>
        </div>

        {/* Issues List */}
        {!loading && issues.length > 0 && viewMode === 'list' && (
          <div className="space-y-3">
            {issues.map((issue) => (
              <IssueCard
                key={issue.id}
                issue={issue as any}
                onOpenMenu={handleOpenMenu}
                showTimeTracker={true}
                showProjectName={shouldShowProjectName}
              />
            ))}
          </div>
        )}

        {/* Issues Table - compact: ID, Subject, Status, Assignee, Actions */}
        {/* Use virtualized table for better performance with large lists (50+ issues) */}
        {!loading && issues.length > 0 && viewMode === 'table' && (
          <>
            {issues.length >= 50 ? (
              <VirtualizedIssuesTable
                issues={issues}
                shouldShowProjectName={shouldShowProjectName}
                onOpenMenu={handleOpenMenu}
              />
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-background">
                    <tr>
                      <th
                        className="px-2 py-2 text-left font-medium text-muted-foreground w-16 cursor-pointer hover:text-foreground transition-colors group"
                        onClick={() => handleInlineTableSort('id')}
                      >
                        {t('tableHeaderId')}
                        {inlineTableSort.column !== 'id' ? (
                          <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-0 group-hover:opacity-50 transition-opacity" />
                        ) : inlineTableSort.direction === 'asc' ? (
                          <ArrowUp className="h-3 w-3 ml-1 inline" />
                        ) : (
                          <ArrowDown className="h-3 w-3 ml-1 inline" />
                        )}
                      </th>
                      <th
                        className="px-2 py-2 text-left font-medium text-muted-foreground max-w-xs lg:max-w-md cursor-pointer hover:text-foreground transition-colors group"
                        onClick={() => handleInlineTableSort('subject')}
                      >
                        {t('tableHeaderSubject')}
                        {inlineTableSort.column !== 'subject' ? (
                          <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-0 group-hover:opacity-50 transition-opacity" />
                        ) : inlineTableSort.direction === 'asc' ? (
                          <ArrowUp className="h-3 w-3 ml-1 inline" />
                        ) : (
                          <ArrowDown className="h-3 w-3 ml-1 inline" />
                        )}
                      </th>
                      <th
                        className="px-2 py-2 text-left font-medium text-muted-foreground w-36 cursor-pointer hover:text-foreground transition-colors group"
                        onClick={() => handleInlineTableSort('status')}
                      >
                        {t('tableHeaderStatus')}
                        {inlineTableSort.column !== 'status' ? (
                          <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-0 group-hover:opacity-50 transition-opacity" />
                        ) : inlineTableSort.direction === 'asc' ? (
                          <ArrowUp className="h-3 w-3 ml-1 inline" />
                        ) : (
                          <ArrowDown className="h-3 w-3 ml-1 inline" />
                        )}
                      </th>
                      <th
                        className="px-2 py-2 text-left font-medium text-muted-foreground w-44 hidden md:table-cell cursor-pointer hover:text-foreground transition-colors group"
                        onClick={() => handleInlineTableSort('assignee')}
                      >
                        {t('tableHeaderAssignee')}
                        {inlineTableSort.column !== 'assignee' ? (
                          <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-0 group-hover:opacity-50 transition-opacity" />
                        ) : inlineTableSort.direction === 'asc' ? (
                          <ArrowUp className="h-3 w-3 ml-1 inline" />
                        ) : (
                          <ArrowDown className="h-3 w-3 ml-1 inline" />
                        )}
                      </th>
                      <th className="px-2 py-2 text-center font-medium text-muted-foreground w-12 sticky right-0 bg-background">{t('tableHeaderActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {sortedInlineIssues.map((issue) => (
                      <tr key={issue.id} className="hover:bg-muted/5">
                        <td className="px-2 py-2 align-middle text-xs text-muted-foreground">{issue.id}</td>
                        <td className="px-2 py-2 align-middle max-w-xs lg:max-w-md">
                          <Link href={`/issues/${issue.id}`} className="font-medium hover:underline block">
                            <div className="truncate">{issue.subject}</div>
                            {/* Project name under the issue title (conditional) */}
                            {shouldShowProjectName && (
                              <div className="text-xs text-muted-foreground mt-1 truncate">{issue.project?.name}</div>
                            )}
                          </Link>
                        </td>
                        <td className="px-2 py-2 align-middle">
                          {issue.status && (
                            <Badge
                              variant={getStatusBadgeStyle(issue.status.name, issue.status.is_closed).variant}
                              className={`text-[10px] font-normal whitespace-nowrap ${getStatusBadgeStyle(issue.status.name, issue.status.is_closed).className}`}
                            >
                              {issue.status.name}
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-2 align-middle text-sm whitespace-nowrap hidden md:table-cell">{issue.assigned_to?.name || '—'}</td>
                        <td className="px-2 py-2 align-middle sticky right-0 bg-background/95 backdrop-blur-sm">
                          <div className="flex justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setSidebarMenu({ show: true, issue })
                                setMenuData({ statuses, priorities, trackers, members: users })
                              }}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Issues Grid */}
        {!loading && issues.length > 0 && viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {issues.map((issue) => (
              <IssueCardGrid
                key={issue.id}
                issue={issue as any}
                onOpenMenu={handleOpenMenu}
                showProjectName={shouldShowProjectName}
              />
            ))}
          </div>
        )}

        {!loading && issues.length === 0 && (
          <Card className="border-2 border-dashed border-muted-foreground/20">
            <CardContent className="py-16 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg">
                    {searchTerm || getActiveFilterCount() > 0
                      ? t('noIssuesFound')
                      : t('noIssuesYet')
                    }
                  </h3>
                  <p className="text-muted-foreground">
                    {searchTerm || getActiveFilterCount() > 0
                      ? t('tryAdjustingFilters')
                      : showProjectSelector && selectedProjectId === 'all'
                      ? t('noIssuesAcrossAllProjects')
                      : t('createFirstIssue')
                    }
                  </p>
                </div>
                <div className="flex space-x-2">
                  {(searchTerm || getActiveFilterCount() > 0) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchTerm('')
                        clearFilters()
                      }}
                    >
                      Clear Search & Filters
                    </Button>
                  )}
                  {!showProjectSelector && projectId && (
                    <Button onClick={() => setShowCreateIssueModal(true)}>
                      <Plus className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                      Create Issue
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {!loading && totalCount > itemsPerPage && (
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} issues
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalCount / itemsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(totalCount / itemsPerPage)
                  let pageNum: number

                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-10"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  )
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / itemsPerPage), prev + 1))}
                disabled={currentPage === Math.ceil(totalCount / itemsPerPage)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Filter Sidebar */}
      <IssueFilterSidebar
        isOpen={showFilters}
        onClose={handleCloseFilters}
        filters={filters}
        onFiltersChange={setFilters}
        trackers={trackers}
        statuses={statuses}
        priorities={priorities}
        users={users}
        versions={versions}
        currentUser={currentUser}
      />

      {/* Create Issue Modal */}
      {!showProjectSelector && projectId && showCreateIssueModal && (
        <EditIssueModal
          isOpen={showCreateIssueModal}
          onClose={() => {
            setShowCreateIssueModal(false)
            setSelectedIssue(null)
          }}
          parentIssueId={selectedIssue?.id}
          projectId={Number(projectId)}
          onSuccess={() => {
            setShowCreateIssueModal(false)
            setSelectedIssue(null)
            fetchIssues()
          }}
        />
      )}

      {/* Issue Actions Modal */}
      {sidebarMenu.show && sidebarMenu.issue && (
        <IssueActionsModal
            issue={sidebarMenu.issue as any}
            isOpen={sidebarMenu.show}
            onClose={() => setSidebarMenu({ show: false, issue: null })}
            projectId={sidebarMenu.issue.project?.id}
            onEditClick={() => {
              setIssueToEdit(sidebarMenu.issue)
              setShowEditIssueModal(true)
              setSidebarMenu({ show: false, issue: null })
            }}
            onActionComplete={async () => {
              setSidebarMenu({ show: false, issue: null })

              // Wait for Firebase to process the update (same delay as sprint view)
              await new Promise(resolve => setTimeout(resolve, 500))

              // Refetch to get updated data
              fetchIssues()
            }}
          />
      )}

      {/* Edit Issue Modal */}
      {showEditIssueModal && issueToEdit && (
        <EditIssueModal
          issue={issueToEdit as any}
          isOpen={showEditIssueModal}
          onClose={() => {
            setShowEditIssueModal(false)
            setIssueToEdit(null)
          }}
          projectId={issueToEdit.project.id}
          onSuccess={(updatedIssue) => {
            setShowEditIssueModal(false)
            setIssueToEdit(null)
            
            // Optimistically update the issue in the local state
            if (updatedIssue) {
              setIssues(prevIssues => 
                prevIssues.map(issue => 
                  issue.id === updatedIssue.id ? { ...issue, ...updatedIssue } : issue
                )
              )
            }
            
            // Also fetch fresh data to ensure consistency
            fetchIssues()
          }}
        />
      )}
    </>
  )
}
